// Single source of truth for "is this an openable external link" — used by every
// adapter that turns an agent-supplied string into a clickable card / window.open
// (mark_as_suggested -> handleMarkAsSuggested, open_url -> handleOpenUrl).
//
// The agent (LLM) emits action_url freely; its tool definition only asks for an
// "echte URL", with no absolute-https requirement. So a scheme-less host
// ("www.winterbach.de/...") or a relative path ("/buergerservice") can arrive.
// window.open() resolves those against the EMBEDDING origin (heyai.world), which
// opened the blank pages Samuel reported. This coerces what is safely coercible
// (scheme-less host -> https) and rejects what is not (relative path, non-http(s)).

export type NormalizedUrl =
  | { ok: true; url: string }
  | { ok: false; reason: "missing_url" | "invalid_url" | "unsupported_url_protocol" };

// Matches a real scheme prefix like "https://" / "ftp://" (requires the "//").
const SCHEME_PREFIX = /^[a-z][a-z0-9+.-]*:\/\//i;
// Matches a bare host like "www.winterbach.de", "winterbach.de/path" — needs a dot.
const SCHEMELESS_HOST = /^[a-z0-9-]+(\.[a-z0-9-]+)+([/?#]|$)/i;
// Agent sometimes wraps the URL in markdown: "[label](https://...)".
const MARKDOWN_URL = /\((https?:\/\/[^\s)]+)\)/i;

export function normalizeActionUrl(raw: unknown): NormalizedUrl {
  if (typeof raw !== "string") return { ok: false, reason: "missing_url" };

  let candidate = raw.trim();
  if (!candidate) return { ok: false, reason: "missing_url" };

  const wrapped = candidate.match(MARKDOWN_URL);
  if (wrapped) candidate = wrapped[1];

  // Scheme-less host (not a relative "/path") -> assume https.
  if (!SCHEME_PREFIX.test(candidate) && !candidate.startsWith("/") && SCHEMELESS_HOST.test(candidate)) {
    candidate = `https://${candidate}`;
  }

  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    // Relative path or unparseable — cannot be safely opened as an external link.
    return { ok: false, reason: "invalid_url" };
  }

  if (!["http:", "https:"].includes(url.protocol)) {
    return { ok: false, reason: "unsupported_url_protocol" };
  }

  return { ok: true, url: url.toString() };
}
